import { IImportCSVMintFailure, IImportCSVMintSummary, IOfficeholder } from "@redbelly-tokeniser/mystake-api-sdk";
import {
  IFundraiseConfig,
  IInvestorsListData,
  ILiquidiseConfig,
  INotesCertificateConfig,
  IShareCertificateConfig,
  IDistributionConfig,
  IRedemptionConfig,
  IProtocolSettings,
  ISubscriptionConfig,
  IUserCompanyRoles,
  IUserRolesByCompany,
  RegistryType,
  Roles,
  ShareTokenisationStandard,
  ShareholderStatus,
  getMockUserCompanyRoles,
  FeatureIds,
  defaultTier,
} from "@redbelly-tokeniser/mystake-common-sdk";
import React, { useCallback, useEffect, useRef, useState, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../../auth/AuthProvider";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_ORDER,
  Order,
  Tier,
  defaultNotesCertificateConfig,
  defaultShareCertificateConfig,
} from "../../constants/constants";
import { companyService } from "../../util";
import { useToast } from "../../designSystem";
import { errorMessage } from "../commons/errorHandler";
import { attachCsvImportMintSocketListener } from "../utility/hooks/useCsvImportMintSocket";
import { getBaseServerUrl, initialiseSocket } from "../utility/hooks/useSocketClient";
import { mergeOfficeHolders } from "../utility/officeholders";
import { CsvImportMintFailuresDialog } from "./CsvImportMintFailuresDialog";
import {
  isFeatureOn,
  isLegacyPdtiCompany as checkIsLegacyPdtiCompany,
  resolveCompanyFeatures,
} from "./pdtiFeatureFlags";

interface CompanyContextType {
  companyId: string;
  companyName: string;
  setCompanyName: (e: string) => void;
  acn: string;
  setAcn: (e: string) => void;
  entityType: string;
  setEntityType: (e: string) => void;
  trusteeType: string;
  setTrusteeType: (e: string) => void;
  isAsicConnectionActivated: boolean;
  setAsicConnectionActivated: (e: boolean) => void;
  isLoadingCompanyInfo: boolean;
  setIsLoadingCompanyInfo: (e: boolean) => void;
  selectedSubscriptionPlan: string;
  setSelectedSubscriptionPlan: (planName: string) => void;
  selectedTier: Tier;
  setSelectedTier: (tierName: Tier) => void;
  displayName: string;
  setDisplayName: (name: string) => void;
  displayLogo: string | undefined;
  setDisplayLogo: (url: string) => void;
  refreshCompanyData: (newCompanyId?: string) => void;
  loadCompanyInfo: () => Promise<void>;
  companyType: string;
  activeShareholders: number;
  setActiveShareholders: (count: number) => void;
  features: { [key: string]: boolean };
  /**
   * Persisted registry classification for the current company. Unset stored values are exposed as
   * `RegistryType.Company` through `effectiveRegistryType` on the API side. This context field is
   * the runtime source of truth for fund-versus-company behaviour.
   */
  registryType: RegistryType;
  /** True when `registryType` is Fund Registry — unit wording, decimal quantities, fund-only gates. */
  isFundRegistry: boolean;
  isLegacyPdtiCompany: boolean;
  dateOfRegistration: Date | null;
  isAgentAuthorized: boolean;
  fundraiseConfig: IFundraiseConfig | undefined;
  liquidiseConfig?: ILiquidiseConfig;
  distributionConfig?: IDistributionConfig;
  setDistributionConfig: (config: IDistributionConfig | undefined) => void;
  redemptionConfig?: IRedemptionConfig;
  /** The fund's accrual switch, read with the company so investor screens can see it too. */
  accrues: boolean;
  /** The timezone fund calendar days are counted in; unset until the company has loaded. */
  fundTimeZone?: string;
  subscriptionConfig?: ISubscriptionConfig;
  shareTokenisationStandard: ShareTokenisationStandard;
  shareCertificateConfig?: IShareCertificateConfig;
  setShareCertificateConfig: (data: IShareCertificateConfig | undefined) => void;
  notesCertificateConfig?: INotesCertificateConfig;
  setNotesCertificateConfig: (data: INotesCertificateConfig | undefined) => void;
  isSharesLockedForTrading: boolean;
  isOptionsLockedForTrading: boolean;
  refreshTradingData: () => void;
  companyContactEmail: string;
  /** Lowercase Claim Issuer ONCHAINID. Empty string when unset. */
  claimIssuerOnchainId: string;
  /** Pre-deploy identity protocol draft from the company document. */
  protocolSettings?: IProtocolSettings;
  setProtocolSettings: (settings: IProtocolSettings | undefined) => void;
  officeholders: { DIR: IOfficeholder[]; SEC: IOfficeholder[] };
  getOfficeholderDetails: (id: string) => IOfficeholder | undefined;
  displayRequestBadge: boolean;
  setDisplayRequestBadge: (displayRequestBadge: boolean) => void;
  userCompanyRoles: IUserCompanyRoles;
  investorRequests: {
    pending: IInvestorsListData | undefined;
    declined: IInvestorsListData | undefined;
  };
  getInvestorRequests: (
    keyword?: string,
    page?: number,
    rowsPerPage?: number,
    sortBy?: string,
    sortOrder?: Order,
    status?: ShareholderStatus[]
  ) => Promise<
    | {
        pending: IInvestorsListData;
        declined: IInvestorsListData;
      }
    | undefined
  >;
  activeRequestsList: IInvestorsListData | undefined;
  setActiveRequestsList: (data: IInvestorsListData | undefined) => void;
  trackCsvImportMintBatch: (mintBatchId: string) => void;
  csvImportMintRefreshKey: number;
  csvImportMintFailures: IImportCSVMintFailure[] | null;
  clearCsvImportMintFailures: () => void;
}

export const CompanyContext = React.createContext<CompanyContextType>({
  companyId: "",
  companyName: "",
  setCompanyName: () => {},
  acn: "",
  setAcn: () => {},
  entityType: "",
  setEntityType: () => {},
  trusteeType: "",
  setTrusteeType: () => {},
  isAsicConnectionActivated: false,
  setAsicConnectionActivated: () => {},
  isLoadingCompanyInfo: false,
  setIsLoadingCompanyInfo: () => {},
  selectedSubscriptionPlan: "",
  setSelectedSubscriptionPlan: () => {},
  selectedTier: "tier4",
  setSelectedTier: () => {},
  displayName: "",
  setDisplayName: () => {},
  displayLogo: "",
  setDisplayLogo: () => {},
  refreshCompanyData: () => {},
  loadCompanyInfo: async () => {},
  companyType: "",
  activeShareholders: 0,
  setActiveShareholders: () => {},
  features: {},
  registryType: RegistryType.Company,
  isFundRegistry: false,
  isLegacyPdtiCompany: false,
  dateOfRegistration: null,
  isAgentAuthorized: false,
  fundraiseConfig: undefined,
  distributionConfig: undefined,
  setDistributionConfig: () => {},
  redemptionConfig: undefined,
  accrues: false,
  fundTimeZone: undefined,
  subscriptionConfig: undefined,
  liquidiseConfig: undefined,
  shareTokenisationStandard: ShareTokenisationStandard.Legacy,
  shareCertificateConfig: defaultShareCertificateConfig,
  setShareCertificateConfig: () => {},
  notesCertificateConfig: defaultNotesCertificateConfig,
  setNotesCertificateConfig: () => {},
  isSharesLockedForTrading: false,
  isOptionsLockedForTrading: false,
  refreshTradingData: () => {},
  companyContactEmail: "",
  claimIssuerOnchainId: "",
  protocolSettings: undefined,
  setProtocolSettings: () => {},
  officeholders: { DIR: [], SEC: [] },
  getOfficeholderDetails: () => undefined,
  displayRequestBadge: false,
  setDisplayRequestBadge: () => {},
  userCompanyRoles: getMockUserCompanyRoles(),
  investorRequests: { pending: undefined, declined: undefined },
  getInvestorRequests: async () => undefined,
  activeRequestsList: undefined,
  setActiveRequestsList: () => {},
  trackCsvImportMintBatch: () => {},
  csvImportMintRefreshKey: 0,
  csvImportMintFailures: null,
  clearCsvImportMintFailures: () => {},
});

export const useCompanyContext = () => React.useContext(CompanyContext);

export const useFeatureFlag = (id: FeatureIds): boolean => isFeatureOn(useCompanyContext().features, id);

export const CompanyContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const [companyName, setCompanyName] = React.useState("");
  const [acn, setAcn] = React.useState("");
  const [entityType, setEntityType] = React.useState<string>("");
  const [trusteeType, setTrusteeType] = React.useState<string>("");
  const [displayName, setDisplayName] = React.useState<string>("");
  const [displayLogo, setDisplayLogo] = React.useState<string | undefined>(undefined);
  const [isAsicConnectionActivated, setAsicConnectionActivated] = React.useState<boolean>(false);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = React.useState<string>("");
  const [selectedTier, setSelectedTier] = React.useState<Tier>("tier4");
  const [activeShareholders, setActiveShareholders] = React.useState<number>(0);
  const [features, setFeatures] = React.useState<{ [key: string]: boolean }>({});
  const [registryType, setRegistryType] = React.useState<RegistryType>(RegistryType.Company);
  const [isLegacyPdtiCompany, setIsLegacyPdtiCompany] = React.useState(false);
  const [companyType, setCompanyType] = React.useState("");
  const [dateOfRegistration, setDateOfRegistration] = React.useState<Date | null>(null);
  const [isAgentAuthorized, setIsAgentAuthorized] = React.useState(false);
  const [isLoadingCompanyInfo, setIsLoadingCompanyInfo] = React.useState(true);
  const [fundraiseConfig, setFundraiseConfig] = React.useState<IFundraiseConfig | undefined>(undefined);
  const [distributionConfig, setDistributionConfig] = React.useState<IDistributionConfig | undefined>(undefined);
  const [redemptionConfig, setRedemptionConfig] = React.useState<IRedemptionConfig | undefined>(undefined);
  const [accrues, setAccrues] = React.useState(false);
  const [fundTimeZone, setFundTimeZone] = React.useState<string | undefined>(undefined);
  const [subscriptionConfig, setSubscriptionConfig] = React.useState<ISubscriptionConfig | undefined>(undefined);
  const [liquidiseConfig, setLiquidiseConfig] = React.useState<ILiquidiseConfig | undefined>(undefined);
  const [shareTokenisationStandard, setShareTokenisationStandard] = React.useState<ShareTokenisationStandard>(
    ShareTokenisationStandard.Legacy
  );
  const [shareCertificateConfig, setShareCertificateConfig] = React.useState<IShareCertificateConfig | undefined>(
    undefined
  );
  const [notesCertificateConfig, setNotesCertificateConfig] = React.useState<INotesCertificateConfig | undefined>(
    undefined
  );
  const [companyContactEmail, setCompanyContactEmail] = React.useState<string>("");
  const [claimIssuerOnchainId, setClaimIssuerOnchainId] = React.useState<string>("");
  const [protocolSettings, setProtocolSettings] = React.useState<IProtocolSettings | undefined>(undefined);

  const [officeholders, setOfficeholders] = React.useState<{ DIR: IOfficeholder[]; SEC: IOfficeholder[] }>({
    DIR: [],
    SEC: [],
  });

  const [displayRequestBadge, setDisplayRequestBadge] = useState<boolean>(false);
  const [userCompanyRoles, setUserCompanyRoles] = useState<IUserCompanyRoles>(getMockUserCompanyRoles());
  const [investorRequests, setInvestorRequests] = useState<{
    pending: IInvestorsListData;
    declined: IInvestorsListData;
  }>({ pending: { count: 0, investors: [] }, declined: { count: 0, investors: [] } });
  const [activeRequestsList, setActiveRequestsList] = useState<IInvestorsListData | undefined>(undefined);
  const [csvImportMintRefreshKey, setCsvImportMintRefreshKey] = useState(0);
  const [csvImportMintFailures, setCsvImportMintFailures] = useState<IImportCSVMintFailure[] | null>(null);
  const pendingCsvImportMintBatchesRef = useRef(new Set<string>());
  const completedCsvImportMintBatchesRef = useRef(new Map<string, IImportCSVMintSummary>());

  const { enqueue } = useToast();
  const { currentUser } = useAuthContext();
  const baseUrl = getBaseServerUrl();

  const matchCompanyId = location.pathname.match(/companies-.*?(?=(?:\/))/);
  let companyId = matchCompanyId ? matchCompanyId[0] : "";

  const getUserCompanyRoles = useCallback((): IUserCompanyRoles => {
    const companyRoles = currentUser?.rolesByCompany?.find((role: IUserRolesByCompany) => role.companyId === companyId);
    const checkRole = (role: Roles) => (companyRoles ? companyRoles.roles.includes(role) : false);

    return {
      isSuperAdmin: checkRole(Roles.SUPER_ADMIN) && !currentUser?.isInvestor,
      isAdmin: (checkRole(Roles.ADMIN) || checkRole(Roles.SUPER_ADMIN)) && !currentUser?.isInvestor,
      isApprover: checkRole(Roles.APPROVER) && !currentUser?.isInvestor,
      isInvestor: checkRole(Roles.SHAREHOLDER) && currentUser?.isInvestor,
      isExternalInvestor: checkRole(Roles.EXTERNAL_INVESTOR) && currentUser?.isInvestor,
      isConcierge: checkRole(Roles.CONCIERGE),
      isNominee: checkRole(Roles.NOMINEE),
      isDirector: checkRole(Roles.DIRECTOR),
      isCompanySecretary: checkRole(Roles.COMPANY_SECRETARY),
    };
  }, [companyId, currentUser?.rolesByCompany, currentUser?.isInvestor]);

  const loadCompanyInfo = async () => {
    setIsLoadingCompanyInfo(true);
    if (!companyId) {
      // Outside a company-scoped route: fall out of the "loading" state so the
      // default Tokeniser lockup shows in place of the (never-arriving) custom logo.
      setDisplayLogo("");
      setIsLoadingCompanyInfo(false);
      return;
    }
    try {
      const result = await companyService.getCompanyInfo(companyId);
      const roles = getUserCompanyRoles();
      setUserCompanyRoles(roles);

      let officeholdersResult: { DIR: IOfficeholder[]; SEC: IOfficeholder[] } = { DIR: [], SEC: [] };
      if (roles.isAdmin) officeholdersResult = (await companyService.getOfficeholders(companyId))?.payload;

      const company = result.payload;
      setCompanyName(company.name);
      setAcn(company.acn);
      // Normalise to "" when the company has no custom logo so the fallback
      // Tokeniser lockup renders (Logo treats undefined as "still loading").
      setDisplayLogo(company.displayLogo ?? "");
      setDisplayName(company.displayName);
      setAsicConnectionActivated(company.isAsicConnectionActivated);
      setSelectedSubscriptionPlan(company.stripeData.selectedSubscriptionPlan);
      setSelectedTier(defaultTier(company.stripeData.selectedSubscriptionPlan, company.stripeData.selectedTier));
      setCompanyType(company.companyType);
      setActiveShareholders(company.activeShareholders ?? 0);
      const companyCreatedAt = (company as { createdAt?: Date | string }).createdAt;
      setIsLegacyPdtiCompany(checkIsLegacyPdtiCompany(companyCreatedAt));
      // Fund-versus-company behaviour is sourced from the persisted `registryType` classification.
      const effectiveRegistryType =
        company.registryType === RegistryType.Fund || company.registryType === RegistryType.Security
          ? company.registryType
          : RegistryType.Company;
      setRegistryType(effectiveRegistryType);
      setFeatures(resolveCompanyFeatures(company.features, companyCreatedAt));
      setDateOfRegistration(company.dateOfRegistration || null);
      setIsAgentAuthorized(company.isAgentAuthorized || false);
      setFundraiseConfig(company.fundraiseConfig ?? undefined);
      setDistributionConfig(company.distributionConfig ?? undefined);
      setRedemptionConfig(company.redemptionConfig ?? undefined);
      setAccrues(company.accrualConfig?.accrues === true);
      setFundTimeZone(company.fundTimeZone);
      setSubscriptionConfig(company.subscriptionConfig ?? undefined);
      setLiquidiseConfig(company.liquidiseConfig ?? undefined);
      setShareTokenisationStandard(company.shareTokenisationStandard ?? ShareTokenisationStandard.Legacy);
      setShareCertificateConfig(company.shareCertificateConfig ?? undefined);
      setNotesCertificateConfig(company.notesCertificateConfig ?? undefined);
      setCompanyContactEmail(company.companyContactEmail ?? company.primaryAdminEmail ?? "");
      setClaimIssuerOnchainId(company.claimIssuerOnchainId?.trim() ?? "");
      setProtocolSettings(company.protocolSettings);
      setOfficeholders({ ...officeholdersResult });
    } catch (error) {
      if (currentUser) {
        // A real failure for an authenticated user: fall out of the "loading"
        // logo state so headers render the Tokeniser fallback instead of
        // hanging blank. Pre-auth transient errors (no currentUser yet) leave
        // displayLogo untouched so the eventual successful load does not flash
        // the default lockup before swapping in the company logo.
        setDisplayLogo("");
        enqueue({
          tone: "error",
          title: errorMessage(error, "An error occurred retrieving your information."),
        });
      }
    }
    setIsLoadingCompanyInfo(false);
  };

  const getCompanyInfo = useCallback(loadCompanyInfo, [companyId, currentUser, enqueue, getUserCompanyRoles]);

  const getOfficeholderDetails = useCallback(
    (id: string) => mergeOfficeHolders(officeholders).find((officeholder) => officeholder._id === id),
    [officeholders]
  );

  React.useEffect(() => {
    getCompanyInfo();
  }, [getCompanyInfo]);

  const refreshTradingData = () => {};

  const refreshCompanyData = (newCompanyId?: string) => {
    companyId = newCompanyId ?? companyId;
    getCompanyInfo();
  };

  const getInvestorRequests = useCallback(
    async (
      keyword = "",
      page = 1,
      rowsPerPage = DEFAULT_PAGE_SIZE,
      sortBy = "createdAt",
      sortOrder: Order = DEFAULT_SORT_ORDER,
      status: ShareholderStatus[] = [ShareholderStatus.PENDING, ShareholderStatus.DECLINED]
    ) => {
      try {
        if (!companyId || !userCompanyRoles.isAdmin) return undefined;
        if (status.includes(ShareholderStatus.ACCEPTED)) throw new Error("Investor request status is invalid.");
        setDisplayRequestBadge(false);

        const declinedInvestorRequest = await companyService.getAllInvestors(
          companyId,
          keyword,
          page,
          rowsPerPage,
          sortBy,
          sortOrder,
          [ShareholderStatus.DECLINED]
        );

        const pendingInvestorRequest = await companyService.getAllInvestors(
          companyId,
          keyword,
          page,
          rowsPerPage,
          sortBy,
          sortOrder,
          [ShareholderStatus.PENDING]
        );

        if (pendingInvestorRequest.payload.count > 0) {
          setDisplayRequestBadge(true);
        }

        /**
         * status can be either pending or declined or both or neither
         * only update setInvestorRequests to include either declinedInvestorRequest or pendingInvestorRequest based on that
         */
        const refreshedLists = {
          pending: status.includes(ShareholderStatus.PENDING)
            ? pendingInvestorRequest.payload
            : { count: pendingInvestorRequest.payload.count, investors: [] },
          declined: status.includes(ShareholderStatus.DECLINED)
            ? declinedInvestorRequest.payload
            : { count: declinedInvestorRequest.payload.count, investors: [] },
        };

        setInvestorRequests(refreshedLists);
        return refreshedLists;
      } catch (error) {
        enqueue({
          tone: "error",
          title: errorMessage(error, "An error occurred retrieving investor requests."),
        });
        return undefined;
      }
    },
    [companyId, enqueue, userCompanyRoles]
  );

  useEffect(() => {
    getInvestorRequests();
  }, [getInvestorRequests]);

  useEffect(() => {
    pendingCsvImportMintBatchesRef.current.clear();
    completedCsvImportMintBatchesRef.current.clear();
  }, [companyId]);

  const clearCsvImportMintFailures = useCallback(() => {
    setCsvImportMintFailures(null);
  }, []);

  const handleCsvImportMintComplete = useCallback(
    (_mintBatchId: string, mintSummary: IImportCSVMintSummary) => {
      if (mintSummary.failures?.length) {
        setCsvImportMintFailures(mintSummary.failures);
        enqueue({
          tone: "warning",
          title: `Imported, but ${mintSummary.failed} of ${mintSummary.attempted} token mints failed. See details in the dialog.`,
        });
      } else if (mintSummary.attempted > 0) {
        enqueue({ tone: "success", title: `Imported and ${mintSummary.succeeded} token mints completed` });
      }
      setCsvImportMintRefreshKey((current) => current + 1);
    },
    [enqueue]
  );

  const trackCsvImportMintBatch = useCallback(
    (mintBatchId: string) => {
      const bufferedSummary = completedCsvImportMintBatchesRef.current.get(mintBatchId);
      if (bufferedSummary) {
        completedCsvImportMintBatchesRef.current.delete(mintBatchId);
        handleCsvImportMintComplete(mintBatchId, bufferedSummary);
        return;
      }

      pendingCsvImportMintBatchesRef.current.add(mintBatchId);
    },
    [handleCsvImportMintComplete]
  );

  useEffect(() => {
    if (!baseUrl || !userCompanyRoles.isAdmin) {
      return () => {};
    }

    let socket: Awaited<ReturnType<typeof initialiseSocket>> | undefined;
    let detachCsvImportMintListener: (() => void) | undefined;
    let cancelled = false;

    initialiseSocket(userCompanyRoles.isAdmin, companyId)
      .then((connectedSocket) => {
        if (cancelled) {
          connectedSocket.disconnect();
          return;
        }

        socket = connectedSocket;

        connectedSocket.on("investorRequest", () => {
          try {
            getInvestorRequests();
          } catch (error) {
            connectedSocket.emit("clientError", { error: JSON.stringify(error) });
          }
        });

        detachCsvImportMintListener = attachCsvImportMintSocketListener(
          connectedSocket,
          companyId,
          () => pendingCsvImportMintBatchesRef.current,
          () => completedCsvImportMintBatchesRef.current,
          handleCsvImportMintComplete
        );
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      detachCsvImportMintListener?.();
      socket?.disconnect();
    };
  }, [baseUrl, companyId, getInvestorRequests, handleCsvImportMintComplete, userCompanyRoles.isAdmin]);

  return (
    <CompanyContext.Provider
      value={{
        companyId,
        companyName,
        setCompanyName,
        acn,
        setAcn,
        entityType,
        setEntityType,
        trusteeType,
        setTrusteeType,
        isAsicConnectionActivated,
        setAsicConnectionActivated,
        selectedSubscriptionPlan,
        setSelectedSubscriptionPlan,
        displayName,
        setDisplayName,
        displayLogo,
        setDisplayLogo,
        refreshCompanyData,
        loadCompanyInfo,
        companyType,
        selectedTier,
        setSelectedTier,
        activeShareholders,
        setActiveShareholders,
        features,
        registryType,
        isFundRegistry: registryType === RegistryType.Fund,
        isLegacyPdtiCompany,
        dateOfRegistration,
        isAgentAuthorized,
        fundraiseConfig,
        distributionConfig,
        setDistributionConfig,
        redemptionConfig,
        accrues,
        fundTimeZone,
        subscriptionConfig,
        liquidiseConfig,
        shareTokenisationStandard,
        shareCertificateConfig,
        setShareCertificateConfig,
        notesCertificateConfig,
        setNotesCertificateConfig,
        isLoadingCompanyInfo,
        setIsLoadingCompanyInfo,
        isSharesLockedForTrading: false,
        isOptionsLockedForTrading: false,
        refreshTradingData,
        companyContactEmail,
        claimIssuerOnchainId,
        protocolSettings,
        setProtocolSettings,
        officeholders,
        getOfficeholderDetails,
        displayRequestBadge,
        setDisplayRequestBadge,
        userCompanyRoles,
        investorRequests,
        getInvestorRequests,
        activeRequestsList,
        setActiveRequestsList,
        trackCsvImportMintBatch,
        csvImportMintRefreshKey,
        csvImportMintFailures,
        clearCsvImportMintFailures,
      }}
    >
      {children}
      <CsvImportMintFailuresDialog
        open={csvImportMintFailures !== null && csvImportMintFailures.length > 0}
        failures={csvImportMintFailures ?? []}
        onClose={clearCsvImportMintFailures}
      />
    </CompanyContext.Provider>
  );
};
